import { useState, useEffect, useRef } from "react";
import type { TranslateFn } from "../../i18n";
import type { Profile, ProfileCompletionDraft, ProfileMeasurements } from "../types";
import { UserIcon } from "../icons";
import { CreateProfileWizard } from "./CreateProfileWizard";
import { ProfileMeasurementsView } from "./ProfileMeasurementsView";

interface MySizingProfilesViewProps {
  profiles: Profile[];
  activeProfileId: string | null;
  onSelectProfile: (id: string) => void;
  onEditProfile: (p: Profile) => void;
  onSaveNewProfile: (data: Omit<Profile, "id" | "createdAt">) => string | null | void | Promise<string | null | void>;
  onSaveProfileMeasurements: (id: string, measurements: ProfileMeasurements, unit?: "cm" | "in") => void;
  onSaveBraSize?: (id: string, bandSize: string, cupSize: string) => void;
  onDeleteProfile?: (id: string) => void;
  /** Called when user clicks delete — parent owns the confirmation modal */
  onRequestDelete: (id: string) => void;
  onLogout: () => void;
  onClose: () => void;
  /** Forwarded to CreateProfileWizard for the pre-upload age check. */
  apiUrl?: string;
  apiKey?: string;
  /** Forwarded to CreateProfileWizard — called when a photo is selected or removed */
  onPhotoPreview?: (base64: string | null) => void;
  /** Draft saved from a size result before auth. Starts the photo profile flow after login. */
  initialCreateDraft?: ProfileCompletionDraft | null;
  onProfileDraftConsumed?: () => void;
  /** Forwarded to CreateProfileWizard — called during image path to estimate measurements */
  onEstimateFromPhoto?: (data: {
    photoBase64: string;
    height: number;
    weight: number;
    heightUnit: "cm" | "in";
    weightUnit: "kg" | "lbs";
    gender: "male" | "female";
    age?: number;
    bandSize?: string;
    cupSize?: string;
    braSizeRegion?: string;
    bodyLandmarks?: Record<string, { x: number; y: number }> | null;
  }) => Promise<{ estimates: Record<string, number>; unit: string } | null>;
  /** Set of profile IDs currently running background estimation — drives the
   *  "calculating..." spinner in the profile detail view. */
  estimatingProfileIds?: Set<string>;
  t: TranslateFn;
  /** Called by PrimeStyleTryonInner's back button handler to check if there's
      an internal sub-step to go back to before closing the profiles view. */
  onRegisterBackInterceptor?: (canGoBack: () => boolean, goBack: () => void) => void;
}

function ProfileAvatar({ gender }: { gender?: "male" | "female" }) {
  return (
    <div className="ps-msp-avatar">
      <UserIcon size={28} />
      {gender && (
        <span className={`ps-msp-avatar-tag ps-${gender}`}>
          {gender === "female" ? "♀" : "♂"}
        </span>
      )}
    </div>
  );
}

/** Custom delete confirmation modal — replaces browser window.confirm() */
export function DeleteConfirmModal({
  onConfirm, onCancel, t,
}: {
  onConfirm: () => void;
  onCancel: () => void;
  t: TranslateFn;
}) {
  return (
    <div className="ps-confirm-overlay" onClick={onCancel}>
      <div className="ps-confirm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ps-confirm-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
            <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
          </svg>
        </div>
        <p>{t("Delete this profile?")}</p>
        <small>{t("This cannot be undone.")}</small>
        <div className="ps-confirm-actions">
          <button type="button" className="ps-confirm-cancel" onClick={onCancel}>{t("Cancel")}</button>
          <button type="button" className="ps-confirm-delete" onClick={onConfirm}>{t("Delete")}</button>
        </div>
      </div>
    </div>
  );
}

function ProfileCard({
  profile, isActive, onSelect, onEdit, onDelete, onOpenDetail, t,
}: {
  profile: Profile;
  isActive: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onOpenDetail: () => void;
  t: TranslateFn;
}) {
  const heightDisplay = (() => {
    const h = profile.height ?? profile.heightCm;
    if (!h) return null;
    if (profile.heightUnit === "in" || profile.heightUnit === "ft") {
      const ft = Math.floor(h / 12);
      const inches = Math.round(h % 12);
      return `${ft}'${inches}"`;
    }
    return `${Math.round(h)} cm`;
  })();
  const weightDisplay = (() => {
    const w = profile.weight ?? profile.weightKg;
    if (!w) return null;
    return `${Math.round(w)} ${profile.weightUnit || "kg"}`;
  })();
  const updatedDisplay = (() => {
    const ts = profile.lastEditedAt || profile.lastUsedAt || profile.createdAt;
    if (!ts) return null;
    return new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  })();
  const photoSrc = profile.photoUrl || profile.photoBase64;

  return (
    <div
      className={`ps-msp-card${isActive ? " ps-active" : ""}`}
      onClick={onOpenDetail}
      role="button"
      tabIndex={0}
    >
      <div className="ps-msp-card-header">
        <span className="ps-msp-card-tag">
          {isActive ? t("DEFAULT PROFILE") : (profile.gender === "female" ? t("WOMEN'S FIT") : t("MEN'S FIT"))}
        </span>
      </div>
      <div className="ps-msp-card-circle">
        {photoSrc ? (
          <img src={photoSrc} alt={profile.name} />
        ) : (
          <UserIcon size={32} />
        )}
      </div>
      <div className="ps-msp-card-name">{profile.name}</div>
      <div className="ps-msp-card-meta">
        {heightDisplay && (
          <div className="ps-msp-meta-row">
            <span className="ps-msp-meta-label">{t("HEIGHT")}</span>
            <span className="ps-msp-meta-value">{heightDisplay}</span>
          </div>
        )}
        {weightDisplay && (
          <div className="ps-msp-meta-row">
            <span className="ps-msp-meta-label">{t("WEIGHT")}</span>
            <span className="ps-msp-meta-value">{weightDisplay}</span>
          </div>
        )}
        {updatedDisplay && (
          <div className="ps-msp-meta-row">
            <span className="ps-msp-meta-label">{t("LAST UPDATE")}</span>
            <span className="ps-msp-meta-value">{updatedDisplay}</span>
          </div>
        )}
      </div>
      <div className="ps-msp-card-actions" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className={`ps-msp-card-select${isActive ? " ps-active" : ""}`}
          onClick={onSelect}
        >
          {isActive ? t("SELECTED") : t("SELECT")}
        </button>
        <button type="button" className="ps-msp-card-edit" onClick={onEdit} aria-label={t("Edit")}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
        </button>
        <button
          type="button"
          className="ps-msp-card-delete"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          aria-label={t("Delete")}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
            <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
          </svg>
        </button>
      </div>
    </div>
  );
}

function CreateProfileCard({ onClick, t }: { onClick: () => void; t: TranslateFn }) {
  return (
    <button type="button" className="ps-msp-card ps-msp-card-create" onClick={onClick}>
      <div className="ps-msp-create-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      </div>
      <div className="ps-msp-create-title">{t("Create New Profile")}</div>
      <div className="ps-msp-create-sub">{t("ADD MEASUREMENTS & PHOTO")}</div>
    </button>
  );
}

export function MySizingProfilesView({
  profiles, activeProfileId, onSelectProfile, onEditProfile, onSaveNewProfile, onSaveProfileMeasurements,
  onSaveBraSize, onRequestDelete, onLogout, onClose, apiUrl, apiKey, onPhotoPreview, initialCreateDraft, onProfileDraftConsumed, onEstimateFromPhoto, estimatingProfileIds, t, onRegisterBackInterceptor,
}: MySizingProfilesViewProps) {
  const [creating, setCreating] = useState(() => !!initialCreateDraft);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [justCreatedId, setJustCreatedId] = useState<string | null>(null);
  const handledDraftKeyRef = useRef<string | number | null>(initialCreateDraft?.createdAt ?? null);

  const viewingProfile = viewingId ? profiles.find((p) => p.id === viewingId) || null : null;

  useEffect(() => {
    if (!initialCreateDraft) return;
    const draftKey = initialCreateDraft.createdAt ?? "draft";
    if (handledDraftKeyRef.current === draftKey && creating) return;
    handledDraftKeyRef.current = draftKey;
    setViewingId(null);
    setCreating(true);
  }, [creating, initialCreateDraft]);

  // Close detail view if the profile being viewed was deleted
  useEffect(() => {
    if (viewingId && !profiles.find((p) => p.id === viewingId)) {
      setViewingId(null);
      setJustCreatedId(null);
    }
  }, [profiles, viewingId]);

  void onEditProfile;

  // Fix 5: Register back interceptor so global back button navigates within
  // sub-screens (create wizard / profile detail) instead of closing the view.
  useEffect(() => {
    if (!onRegisterBackInterceptor) return;
    const canGoBack = () => creating || viewingId !== null;
    const goBack = () => {
      if (creating) {
        setCreating(false);
        onProfileDraftConsumed?.();
        onPhotoPreview?.(null);
        return;
      }
      if (viewingId !== null) { setViewingId(null); setJustCreatedId(null); return; }
    };
    onRegisterBackInterceptor(canGoBack, goBack);
  }, [creating, viewingId, onRegisterBackInterceptor, onProfileDraftConsumed, onPhotoPreview]);

  void onClose;

  return (
    <div className="ps-msp-root">
      <div className="ps-msp-scroll">
        {viewingProfile ? (
          <ProfileMeasurementsView
            profile={viewingProfile}
            isActive={viewingProfile.id === activeProfileId}
            isEstimating={estimatingProfileIds?.has(viewingProfile.id) ?? false}
            onSelect={() => { onSelectProfile(viewingProfile.id); setViewingId(null); }}
            onSaveMeasurements={(m, unit) => onSaveProfileMeasurements(viewingProfile.id, m, unit)}
            onSaveBraSize={onSaveBraSize
              ? (band, cup) => onSaveBraSize!(viewingProfile.id, band, cup)
              : undefined}
            primaryLabel={viewingProfile.id === justCreatedId ? "Done" : undefined}
            onDelete={() => onRequestDelete(viewingProfile.id)}
            onBack={() => { setViewingId(null); setJustCreatedId(null); }}
            onSave={() => { setViewingId(null); setJustCreatedId(null); }}
            t={t}
          />
        ) : creating ? (
          <CreateProfileWizard
            key={`create-${initialCreateDraft?.createdAt ?? "blank"}`}
            apiUrl={apiUrl}
            apiKey={apiKey}
            initialMode={initialCreateDraft ? "image" : undefined}
            initialDraft={initialCreateDraft}
            directAnalysisFlow={!!initialCreateDraft}
            onSave={async (data) => {
              const savedId = await onSaveNewProfile(data);
              onProfileDraftConsumed?.();
              setCreating(false);
              if (typeof savedId === "string" && savedId) {
                setJustCreatedId(savedId);
                setViewingId(savedId);
              }
            }}
            onCancel={() => {
              setCreating(false);
              setJustCreatedId(null);
              onProfileDraftConsumed?.();
            }}
            onPhotoPreview={onPhotoPreview}
            onEstimate={onEstimateFromPhoto}
            t={t}
          />
        ) : (
          <div className="ps-msp-home">
            <section className="ps-msp-welcome-panel" aria-label={t("Profile welcome")}>
              <div>
                <div className="ps-msp-kicker">{t("Welcome")}</div>
                <h2 className="ps-msp-title">{t("My Sizing Profiles")}</h2>
                <p className="ps-msp-subtitle">
                  {profiles.length > 0
                    ? t("Choose a saved profile or create a new one before finding your best fit.")
                    : t("Create your first fit profile to save your measurements, photo, and size history.")}
                </p>
              </div>

              <div className="ps-msp-header-actions">
                <button type="button" className="ps-msp-primary-create" onClick={() => setCreating(true)}>
                  {t("Create New Profile")}
                </button>
                <button type="button" className="ps-msp-logout" onClick={onLogout}>
                  {t("Sign out")}
                </button>
              </div>
            </section>

            <section className="ps-msp-cards-panel" aria-label={t("Your profiles")}>
              <div className="ps-msp-cards-scroll">
                <div className="ps-msp-grid">
                  <CreateProfileCard onClick={() => setCreating(true)} t={t} />
                  {profiles.map((p) => (
                    <ProfileCard
                      key={p.id}
                      profile={p}
                      isActive={p.id === activeProfileId}
                      onSelect={() => onSelectProfile(p.id)}
                      onEdit={() => { setJustCreatedId(null); setViewingId(p.id); }}
                      onDelete={() => onRequestDelete(p.id)}
                      onOpenDetail={() => { setJustCreatedId(null); setViewingId(p.id); }}
                      t={t}
                    />
                  ))}
                </div>
              </div>
            </section>
          </div>
        )}
      </div>

    </div>
  );
}
